import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FieldLabel } from '../components/FieldLabel';
import { useNavigate } from 'react-router-dom';
import { sanitizeSearchInput, sanitizeString } from '../utils/security';
import { formatTechServiceLocation } from '../utils/techServiceHelpers';
import {
  fetchUsers,
  searchUsers,
  createUser,
  deleteUser,
  fetchUserStats,
} from '../services/usersService';
import {
  searchTechServices,
  fetchTechServiceDetail,
} from '../services/techServicesService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import { ROWS_PER_PAGE_OPTIONS_NARROW } from '../constants/pagination';
import {
  ORDER_CREATION_PERMISSION_OPTIONS,
  ORDER_CREATION_PERMISSION_DEFAULT,
} from '../constants/orderCreationPermission';
import { USER_ROLE_OPTIONS, getUserRoleLabel } from '../constants/userRoles';

const initialFormState = {
  kullaniciAdi: '',
  sifre: '',
  rol: 'sales',
  techService: '',
  aktif: true,
  isTest: false,
  ad: '',
  soyad: '',
  bayiAdi: '',
  orderCreationPermission: ORDER_CREATION_PERMISSION_DEFAULT,
};

const roleOptions = USER_ROLE_OPTIONS;

// statusFilterOptions kaldırıldı - artık aktif/pasif sekmeleri kullanılıyor

const rowsPerPageOptions = [10, 25, 50];

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) {
    return fallback;
  }

  // 404 hatası için özel mesaj
  if (error?.status === 404) {
    return 'API endpoint bulunamadı. Lütfen backend endpoint\'inin doğru yapılandırıldığından emin olun.';
  }

  // 401/403 hatası için özel mesaj
  if (error?.status === 401) {
    return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  }
  
  if (error?.status === 403) {
    return 'Bu işlem için yetkiniz yok.';
  }

  // Backend'den gelen hatalar array'ini kontrol et
  const hatalar = error?.data?.hatalar;
  if (Array.isArray(hatalar) && hatalar.length > 0) {
    // Tüm hata mesajlarını birleştir
    const hataMesajlari = hatalar
      .map(h => h.mesaj || h.message)
      .filter(Boolean);
    
    if (hataMesajlari.length > 0) {
      return hataMesajlari.join('. ');
    }
  }

  // apiClient.js zaten hatayı işlemiş olabilir, önce error.message'ı kontrol et
  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  // Eğer mesaj zaten işlenmişse (apiClient.js tarafından) direkt döndür
  if (message && typeof message === 'string') {
    // apiClient.js'den gelen işlenmiş mesaj kontrolü
    if (message.includes('Backend\'de bir model hatası oluştu') ||
        message.includes('API endpoint bulunamadı')) {
      return message; // Zaten işlenmiş, direkt döndür
    }
    
    // HTML entity'leri decode et
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  // Backend'den gelen schema/model hatalarını daha anlaşılır mesaja çevir
  if (message && typeof message === 'string') {
    if (message.includes('Schema hasn\'t been registered') || 
        (message.includes('model') && message.includes('hasn\'t been registered')) ||
        message.includes('TechService')) {
      return 'Backend\'de bir model hatası oluştu. Lütfen daha sonra tekrar deneyin veya sistem yöneticisine bildirin.';
    }
    
    // 404 hata mesajlarını yakala
    if (message.toLowerCase().includes('not found') || 
        message.toLowerCase().includes('bulunamadı') ||
        message.toLowerCase().includes('404')) {
      return 'API endpoint bulunamadı. Backend endpoint\'inin doğru yapılandırıldığından emin olun.';
    }
  }

  if (error?.message === 'Failed to fetch' || message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı veya API proxy ayarlarınızı kontrol edin.';
  }

  return message || fallback;
};

// Güvenli tarih parse fonksiyonu
const safeParseDate = (dateValue) => {
  if (!dateValue) return null;
  
  // Zaten Date objesi ise direkt döndür
  if (dateValue instanceof Date) {
    return isNaN(dateValue.getTime()) ? null : dateValue;
  }
  
  // String ise parse et
  if (typeof dateValue === 'string') {
    // ISO format kontrolü
    if (dateValue.match(/^\d{4}-\d{2}-\d{2}/)) {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    // Diğer string formatları
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  
  // Number ise (timestamp)
  if (typeof dateValue === 'number') {
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  
  // Obje ise (MongoDB ObjectId veya Date objesi)
  if (typeof dateValue === 'object') {
    // Eğer Date objesi ise
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }
    // toString() metodu varsa kullan
    if (typeof dateValue.toString === 'function') {
      try {
        const str = dateValue.toString();
        // Eğer "[object Object]" gibi bir şey dönerse, geçersiz
        if (str === '[object Object]') {
          return null;
        }
        const parsed = new Date(str);
        return isNaN(parsed.getTime()) ? null : parsed;
      } catch {
        return null;
      }
    }
  }
  
  return null;
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  try {
    const date = safeParseDate(value);
    if (!date) {
      return '-';
    }
    
    return date.toLocaleString('tr-TR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '-';
  }
};


const roleLabel = (role) => getUserRoleLabel(role);

const UserManagementPage = () => {
  const { confirm } = useConfirmDialog();
  const [activeView, setActiveView] = useState('active'); // 'active' | 'passive' | 'stats'
  const [users, setUsers] = useState([]);
  const [searchInput, setSearchInput] = useState(''); // Arama input'u için ayrı state (debounce için)
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
  });
  const [selectedRole, setSelectedRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsUpdatedAt, setStatsUpdatedAt] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate(); // Gelecekte kullanıcı düzenle sayfası için kullanılacak
  
  // Teknik servis seçim modalı state'leri
  const [isTechServiceModalOpen, setIsTechServiceModalOpen] = useState(false);
  const [techServiceSearchInput, setTechServiceSearchInput] = useState('');
  const [techServiceSearchResults, setTechServiceSearchResults] = useState([]);
  const [techServiceSearchLoading, setTechServiceSearchLoading] = useState(false);
  const [techServiceSearchDebouncing, setTechServiceSearchDebouncing] = useState(false);
  const [techServicePickerOpen, setTechServicePickerOpen] = useState(false);
  const [selectedTechServiceForModal, setSelectedTechServiceForModal] = useState(null);
  const [selectedTechService, setSelectedTechService] = useState(null);
  const [selectedTechServiceName, setSelectedTechServiceName] = useState('');

  const sanitizedSearchValue = useMemo(
    () => sanitizeSearchInput(filters.search),
    [filters.search]
  );

  // Seçili rolleri array'e çevir
  const filterParams = useMemo(
    () => ({
      role: selectedRole === 'all' ? undefined : selectedRole,
      status: activeView === 'active' ? 'active' : activeView === 'passive' ? 'passive' : undefined,
      search: sanitizedSearchValue && sanitizedSearchValue.trim().length > 0 ? sanitizedSearchValue.trim() : undefined,
    }),
    [selectedRole, activeView, sanitizedSearchValue]
  );

  const loadUsers = useCallback(async () => {
    if (activeView === 'stats') {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Arama terimi varsa ve 3+ karakter ise kullan
      const searchParam = filterParams.search && filterParams.search.trim().length >= 3 ? filterParams.search.trim() : undefined;
      const hasActiveFilter = activeView === 'active' || activeView === 'passive';
      const activeStatus = activeView === 'active' ? true : activeView === 'passive' ? false : undefined;
      
      console.log('[UserManagementPage] Loading users with params:', {
        page: currentPage,
        limit: rowsPerPage,
        rol: filterParams.role,
        aktif: activeStatus,
        search: searchParam,
        filterParams: filterParams,
      });
      
      let response;
      
      // Arama endpoint'ini kullan: arama terimi varsa (3+ karakter) VEYA aktif filtresi varsa
      // Backend'de en az bir arama kriteri zorunlu (arama veya aktif)
      const roleParam = filterParams.role;
      if (searchParam || hasActiveFilter) {
        console.log('[UserManagementPage] Using search endpoint');
        response = await searchUsers(searchParam, {
          aktif: activeStatus,
          rol: roleParam,
          page: currentPage,
          limit: rowsPerPage,
        });
      } else {
        // Arama terimi yoksa ve aktif filtresi de yoksa normal listeleme endpoint'ini kullan
        console.log('[UserManagementPage] Using list endpoint');
        response = await fetchUsers({
          page: currentPage,
          limit: rowsPerPage,
          rol: roleParam,
          aktif: activeStatus,
        });
      }
      
      console.log('[UserManagementPage] API Response:', response);

      const { veri } = response || {};
      const dataUsers = veri?.kullanicilar || [];
      
      // Debug: İlk kullanıcının tarih alanlarını logla
      if (dataUsers.length > 0) {
        const firstUser = dataUsers[0];
        console.log('[UserManagementPage] First user date fields:', {
          createdAt: firstUser.createdAt,
          updatedAt: firstUser.updatedAt,
          olusturulmaTarihi: firstUser.olusturulmaTarihi,
          guncellemeTarihi: firstUser.guncellemeTarihi,
          sonGiris: firstUser.sonGiris,
          allFields: Object.keys(firstUser),
        });
      }
      
      // Artık frontend'de filtreleme yapmıyoruz, backend'den zaten filtrelenmiş veriler geliyor
      // Backend'den gelen veriler direkt kullanılıyor
      
      // Pagination bilgilerini al - hem sayfalama objesi hem de direkt veri içindeki alanları kontrol et
      const pagination = veri?.sayfalama || {};
      setUsers(dataUsers);
      setPaginationMeta({
        total: pagination.toplamKullanici || veri?.toplam || dataUsers.length,
        totalPages: pagination.toplamSayfa || veri?.toplamSayfa || Math.ceil((pagination.toplamKullanici || veri?.toplam || dataUsers.length) / rowsPerPage) || 1,
      });
      setCurrentPage(pagination.mevcutSayfa || veri?.sayfa || currentPage);
      setRowsPerPage(pagination.sayfaBasinaSayi || veri?.limit || rowsPerPage);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Kullanıcılar yüklenemedi'));
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [currentPage, rowsPerPage, filterParams, activeView, selectedRole]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await fetchUserStats();
      const { veri } = response || {};
      setStats({
        toplamKullanici: veri?.toplamKullanici ?? 0,
        aktifKullanici: veri?.aktifKullanici ?? 0,
        pasifKullanici: veri?.pasifKullanici ?? 0,
        roleCounts: veri?.rolBazindaSayim ?? [],
      });
      setStatsUpdatedAt(new Date().toISOString());
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'İstatistikler alınamadı'));
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (activeView === 'stats' && !stats) {
      loadStats();
    }
  }, [activeView, stats, loadStats]);

  // Kullanıcı arama debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // 3+ karakter ise arama yap
      if (searchInput.trim().length >= 3) {
        setSearchLoading(true);
        setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
        setCurrentPage(1);
      } else {
        // 2 karakter veya daha az ise filtreyi temizle (normal liste göster)
        setSearchLoading(false);
        setFilters((prev) => ({ ...prev, search: '' }));
        setCurrentPage(1);
      }
    }, 1000); // 1000ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Teknik servis arama: debounce penceresinde "Sonuç yok" flaşını önle
  useEffect(() => {
    if (!isTechServiceModalOpen) {
      setTechServiceSearchDebouncing(false);
      return;
    }
    const term = techServiceSearchInput.trim();
    if (term.length < 3) {
      setTechServiceSearchDebouncing(false);
      return;
    }
    setTechServiceSearchDebouncing(true);
    const tid = setTimeout(() => setTechServiceSearchDebouncing(false), 400);
    return () => clearTimeout(tid);
  }, [techServiceSearchInput, isTechServiceModalOpen]);

  // Teknik servis arama debounce
  useEffect(() => {
    if (!isTechServiceModalOpen) {
      setTechServiceSearchInput('');
      setTechServiceSearchResults([]);
      setSelectedTechServiceForModal(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      const term = techServiceSearchInput.trim();
      if (term.length >= 3) {
        setTechServiceSearchLoading(true);
        try {
          const response = await searchTechServices(term, {
            aktif: true,
          });
          const list = response?.veri?.teknikServisler || [];
          setTechServiceSearchResults(list);
        } catch (err) {
          console.error('Teknik servis arama hatası:', err);
          setTechServiceSearchResults([]);
        } finally {
          setTechServiceSearchLoading(false);
        }
      } else {
        setTechServiceSearchResults([]);
        setTechServiceSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [techServiceSearchInput, isTechServiceModalOpen]);

  const handleSearchChange = (event) => {
    const value = sanitizeSearchInput(event.target.value, { trimEdges: false });
    setSearchInput(value); // searchInput state'ini güncelle (debounce useEffect tetiklenecek)
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setFilters((prev) => ({ ...prev, search: '' }));
    setCurrentPage(1);
  };

  const handleRoleSelectChange = (event) => {
    setSelectedRole(event.target.value);
    setCurrentPage(1);
  };



  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (rowsPerPageOptions.includes(value)) {
      setRowsPerPage(value);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      const nextPage = prev + direction;
      if (nextPage < 1 || nextPage > paginationMeta.totalPages) {
        return prev;
      }
      return nextPage;
    });
  };

  const openCreateForm = () => {
    setFormState(initialFormState);
    setSelectedTechServiceName('');
    setSelectedTechService(null);
    setFormError('');
    setIsFormOpen(true);
  };

  const navigateToUserEdit = (user) => {
    const userId = user?.id || user?._id;
    if (userId) {
      navigate(`/users/${userId}/edit`);
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setFormError('');
    setFormState(initialFormState);
    setSelectedTechServiceName('');
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    setFormState((prev) => {
      const newState = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      
      // Rol değiştiğinde tech-service veya tech-person değilse techService'i temizle
      if (name === 'rol' && value !== 'tech-person' && value !== 'tech-service') {
        newState.techService = '';
      }
      if (name === 'rol' && value !== 'bayi') {
        newState.bayiAdi = '';
      }
      
      return newState;
    });
  };

  // Teknik servis seçim modalı fonksiyonları
  const openTechServiceModal = () => {
    setIsTechServiceModalOpen(true);
    setTechServiceSearchInput('');
    setTechServiceSearchResults([]);
    setSelectedTechServiceForModal(null);
    setTechServicePickerOpen(false);
  };

  const closeTechServiceModal = () => {
    setIsTechServiceModalOpen(false);
    setTechServiceSearchInput('');
    setTechServiceSearchResults([]);
    setSelectedTechServiceForModal(null);
    setTechServicePickerOpen(false);
  };

  const applyTechServiceSelection = async (techService) => {
    if (!techService) return;
    const techServiceId = techService.id || techService._id;
    if (!techServiceId) return;
    const techServiceName = techService.ad || 'Bilinmeyen Teknik Servis';
    setFormState((prev) => ({
      ...prev,
      techService: techServiceId,
    }));
    setSelectedTechServiceName(techServiceName);
    setSelectedTechService(techService);
    closeTechServiceModal();
    try {
      const detailResponse = await fetchTechServiceDetail(techServiceId);
      const detailed =
        detailResponse?.veri?.teknikServis || detailResponse?.veri || techService;
      setSelectedTechService(detailed);
    } catch (error) {
      console.warn('Teknik servis detayı alınamadı:', error);
    }
  };

  const handleTechServiceRowPick = (techService) => {
    void applyTechServiceSelection(techService);
  };

  const handleTechServiceConfirm = async () => {
    if (selectedTechServiceForModal) {
      await applyTechServiceSelection(selectedTechServiceForModal);
    }
  };

  const clearTechServiceSelection = () => {
    setFormState((prev) => ({
      ...prev,
      techService: '',
    }));
    setSelectedTechServiceName('');
    setSelectedTechService(null);
  };

  // Form validasyonu - tüm bilgiler tamamsa kaydet butonu aktif
  const isFormValid = useMemo(() => {
    const username = formState.kullaniciAdi?.trim() || '';
    if (username.length < 3 || username.length > 50) {
      return false;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      return false;
    }

    const password = formState.sifre || '';
    if (password.length < 6 || password.length > 100) {
      return false;
    }

    const ad = formState.ad?.trim() || '';
    if (ad.length > 50) {
      return false;
    }

    const soyad = formState.soyad?.trim() || '';
    if (soyad.length > 50) {
      return false;
    }

    if (!formState.rol) {
      return false;
    }

    if ((formState.rol === 'tech-service' || formState.rol === 'tech-person') && !formState.techService) {
      return false;
    }

    if (formState.rol === 'bayi') {
      const bayiAdi = formState.bayiAdi?.trim() || '';
      if (!bayiAdi || bayiAdi.length > 200) return false;
      if (!ad || !soyad) return false;
    }

    return true;
  }, [formState]);

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormSubmitting(true);
    setFormError('');

    try {
      const username = formState.kullaniciAdi?.trim();
      if (!username || username.length < 3) {
        setFormError('Kullanıcı adı en az 3 karakter olmalıdır');
        return;
      }

      if (username.length > 50) {
        setFormError('Kullanıcı adı en fazla 50 karakter olabilir');
        return;
      }

      if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        setFormError('Kullanıcı adı sadece harf, rakam ve alt çizgi içerebilir');
        return;
      }

      const password = formState.sifre || '';
      if (password.length < 6) {
        setFormError('Şifre en az 6 karakter olmalıdır');
        return;
      }
      if (password.length > 100) {
        setFormError('Şifre en fazla 100 karakter olabilir');
        return;
      }

      const hasLowercase = /[a-z]/.test(password);
      const hasUppercase = /[A-Z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      if (!hasLowercase || !hasUppercase || !hasNumber) {
        setFormError('Şifre en az bir küçük harf, bir büyük harf ve bir rakam içermelidir');
        return;
      }

      if (!formState.rol) {
        setFormError('Rol seçimi zorunludur');
        return;
      }

      if ((formState.rol === 'tech-service' || formState.rol === 'tech-person') && !formState.techService) {
        setFormError('Teknik servis ve teknisyen rollerinde servis seçimi zorunludur');
        return;
      }

      const ad = formState.ad?.trim() || '';
      if (ad.length > 50) {
        setFormError('Ad en fazla 50 karakter olabilir');
        return;
      }

      const soyad = formState.soyad?.trim() || '';
      if (soyad.length > 50) {
        setFormError('Soyad en fazla 50 karakter olabilir');
        return;
      }

      if (formState.rol === 'bayi') {
        const bayiAdi = formState.bayiAdi?.trim() || '';
        if (!bayiAdi) {
          setFormError('Bayi adı zorunludur');
          return;
        }
        if (bayiAdi.length > 200) {
          setFormError('Bayi adı en fazla 200 karakter olabilir');
          return;
        }
        if (!ad || !soyad) {
          setFormError('Bayi rolü için ad ve soyad zorunludur');
          return;
        }
      }

      const payload = {
        kullaniciAdi: username,
        sifre: password,
        rol: formState.rol,
      };

      if (ad) {
        payload.ad = ad;
      }

      if (soyad) {
        payload.soyad = soyad;
      }

      if (formState.techService) {
        payload.techService = formState.techService;
      }

      if (formState.rol === 'tech-person') {
        payload.orderCreationPermission =
          formState.orderCreationPermission || ORDER_CREATION_PERMISSION_DEFAULT;
      }

      if (formState.rol === 'bayi' && formState.bayiAdi?.trim()) {
        payload.bayiAdi = formState.bayiAdi.trim();
      }

      if (formState.isTest) {
        payload.isTest = true;
      }

      console.log('[UserManagement] Creating user with payload:', payload);
      const response = await createUser(payload);
      console.log('[UserManagement] Create user response:', response);

      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Kullanıcı oluşturulamadı');
      }

      if (window.showNotification) {
        window.showNotification('success', 'Kullanıcı başarıyla oluşturuldu');
      }

      const createdUserId =
        response?.veri?.kullanici?.id ||
        response?.veri?.kullanici?._id ||
        response?.veri?.id ||
        response?.veri?.userId;

      closeForm();
      await loadUsers();

      if (createdUserId) {
        navigate(`/users/${createdUserId}/edit`);
      }
    } catch (submitError) {
      console.error('[UserManagement] Form submit error:', submitError);
      console.error('[UserManagement] Error details:', {
        message: submitError?.message,
        status: submitError?.status,
        data: submitError?.data,
        stack: submitError?.stack,
      });
      if (submitError?.status === 404) {
        setFormError('API endpoint bulunamadı. Backend endpoint\'inin doğru yapılandırıldığından emin olun.');
      } else {
        setFormError(getErrorMessage(submitError, 'İşlem gerçekleştirilemedi'));
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    const userId = user.id || user._id;
    const confirmationMessage = `${user.kullaniciAdi} adlı kullanıcıyı silmek istediğinize emin misiniz?`;
    const confirmed = await confirm({
      title: 'Kullanıcıyı sil',
      message: confirmationMessage,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteUser(userId);
      if (window.showNotification) {
        window.showNotification('success', 'Kullanıcı silindi');
      }
      await loadUsers();
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, 'Kullanıcı silinemedi'));
    }
  };

  const handleManualRefresh = () => {
    loadUsers();
  };

  const renderUsersTable = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Kullanıcılar yükleniyor</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }

    if (error) {
      // Error zaten getErrorMessage'den geldiği için decode edilmiş olmalı
      // Ama yine de decode edelim (double decode zararsız)
      const decodedError = typeof error === 'string' 
        ? error
            .replace(/&#x27;/g, "'")
            .replace(/&#39;/g, "'")
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
        : String(error);
      
      return (
        <div className="error-state">
          <div style={{ marginBottom: '10px' }}>{decodedError}</div>
          <button type="button" className="text-link" onClick={handleManualRefresh}>
            Tekrar dene
          </button>
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-search" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Kayıt bulunamadı</div>
        </div>
      );
    }

    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>Kullanıcı Adı</th>
            <th>E-posta</th>
            <th>Telefon</th>
            <th>Rol</th>
            <th>Son Giriş</th>
            <th>Oluşturulma</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id || user._id}>
              <td className="name-cell">
                <span
                  className="tech-service-name-link"
                  onClick={() => navigateToUserEdit(user)}
                  style={{ cursor: 'pointer' }}
                >
                  {safeText(user.kullaniciAdi)}
                  {user.isTest === true && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#92400e',
                        background: '#fef3c7',
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}
                    >
                      Test
                    </span>
                  )}
                </span>
              </td>
              <td>{safeText(user.mail || user.email || '-')}</td>
              <td>{safeText(user.telefon || user.phone || '-')}</td>
              <td>{safeText(roleLabel(user.rol))}</td>
              <td>{formatDate(user.sonGiris)}</td>
              <td>{formatDate(user.createdAt || user.olusturulmaTarihi)}</td>
              <td className="table-actions">
                <button
                  type="button"
                  className="category-action-btn edit-btn"
                  onClick={() => navigateToUserEdit(user)}
                  aria-label="Kullanıcıyı düzenle"
                  title="Düzenle"
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button
                  type="button"
                  className="category-action-btn delete-btn"
                  onClick={() => handleDeleteUser(user)}
                  aria-label="Kullanıcıyı sil"
                  title="Sil"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderStats = () => {
    if (statsLoading || !stats) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">İstatistikler yükleniyor</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }

    return (
      <div className="stats-grid">
        <div className="stat-card stat-card-primary">
          <div className="stat-card-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-card-content">
            <FieldLabel className="stat-label" as="span">Toplam Kullanıcı</FieldLabel>
            <strong className="stat-value">{stats.toplamKullanici}</strong>
          </div>
        </div>

        <div className="stat-card stat-card-success">
          <div className="stat-card-icon">
            <i className="fas fa-user-check"></i>
          </div>
          <div className="stat-card-content">
            <FieldLabel className="stat-label" as="span">Aktif Kullanıcı</FieldLabel>
            <strong className="stat-value">{stats.aktifKullanici}</strong>
          </div>
        </div>

        <div className="stat-card stat-card-warning">
          <div className="stat-card-icon">
            <i className="fas fa-user-times"></i>
          </div>
          <div className="stat-card-content">
            <FieldLabel className="stat-label" as="span">Pasif Kullanıcı</FieldLabel>
            <strong className="stat-value">{stats.pasifKullanici}</strong>
          </div>
        </div>

        <div className="stat-card stat-card-roles">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <i className="fas fa-user-tag"></i>
            </div>
            <FieldLabel className="stat-label" as="span">Rol Bazında Dağılım</FieldLabel>
          </div>
          <div className="stat-card-body">
            {stats.roleCounts.length === 0 ? (
              <div className="stat-empty">Rol verisi bulunamadı</div>
            ) : (
              <ul className="stat-role-list">
                {stats.roleCounts.map((item) => (
                  <li key={item._id} className="stat-role-item">
                    <div className="stat-role-info">
                      <span className="stat-role-label">{roleLabel(item._id)}</span>
                      <span className="stat-role-count">{item.sayi}</span>
                    </div>
                    <div className="stat-role-bar">
                      <div 
                        className="stat-role-bar-fill" 
                        style={{ 
                          width: `${(item.sayi / stats.toplamKullanici) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {statsUpdatedAt && (
          <div className="stat-card stat-card-info">
            <div className="stat-card-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-card-content">
              <FieldLabel className="stat-label" as="span">Son Güncelleme</FieldLabel>
              <strong className="stat-value-small">
                {formatDate(statsUpdatedAt)}
              </strong>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <main className="main-content user-management">
      <div className="content-header">
        <h1>Kullanıcı Yönetimi</h1>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${activeView === 'active' ? 'active' : ''}`}
          onClick={() => {
            if (activeView !== 'active') {
              setActiveView('active');
              setCurrentPage(1);
            }
          }}
        >
          Aktif Kullanıcılar
        </button>
        <button
          type="button"
          className={`tab-btn ${activeView === 'passive' ? 'active' : ''}`}
          onClick={() => {
            if (activeView !== 'passive') {
              setActiveView('passive');
              setCurrentPage(1);
            }
          }}
        >
          Pasif Kullanıcılar
        </button>
        <button
          type="button"
          className={`tab-btn ${activeView === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveView('stats')}
        >
          İstatistikler
        </button>
      </div>

      {activeView !== 'stats' ? (
        <>
          <div className="toolbar">
            <div className="search-filter-bar">
              <div className="search-box">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Kullanıcı ara (min 3 karakter)..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  maxLength={100}
                  autoComplete="off"
                />
                {searchLoading && (
                  <i
                    className="fas fa-spinner fa-spin"
                    style={{ right: searchInput.length > 0 ? '40px' : '10px' }}
                    aria-hidden
                  />
                )}
                {searchInput.length > 0 && (
                  <button
                    type="button"
                    className="search-clear-btn"
                    onClick={handleClearSearch}
                    title="Aramayı temizle"
                    aria-label="Aramayı temizle"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              <div className={`role-filter-dropdown ${selectedRole !== 'all' ? 'filter-active' : ''}`}>
                <i className="fas fa-filter role-filter-icon"></i>
                <select value={selectedRole} onChange={handleRoleSelectChange} aria-label="Rol filtreleme">
                  <option value="all">Tüm roller</option>
                  {roleOptions.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="refresh-btn"
                onClick={handleManualRefresh}
                title="Yenile"
                aria-label="Listeyi yenile"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
            
            <button 
              type="button" 
              className="primary-btn add-user-btn" 
              onClick={openCreateForm}
            >
              <i className="fas fa-plus"></i>
              <span>Kullanıcı Ekle</span>
            </button>
          </div>

          <div className="table-container">{renderUsersTable()}</div>

          <TableListFooter
            totalRecords={paginationMeta.total}
            currentPage={currentPage}
            totalPages={paginationMeta.totalPages || 1}
            onPageChange={setCurrentPage}
            loading={loading}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS_NARROW}
            onRowsPerPageChange={(value) => handleRowsPerPageChange({ target: { value: String(value) } })}
          />
        </>
      ) : (
        <div className="stats-section">
          <div className="toolbar stats-toolbar">
            <button
              type="button"
              className="filter-btn"
              onClick={loadStats}
              disabled={statsLoading}
            >
              <i className="fas fa-sync"></i>
              <span>İstatistikleri Yenile</span>
            </button>
          </div>
          {renderStats()}
        </div>
      )}


      {/* Kullanıcı Ekle/Düzenle Modal */}
      {isFormOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Kullanıcı Ekle</h2>
              <button type="button" className="close-btn" onClick={closeForm} aria-label="Kapat">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="modal-body">
              <div className="form-grid">
                <label className="form-field">
                  <span>Kullanıcı Adı *</span>
                  <input
                    type="text"
                    name="kullaniciAdi"
                    value={formState.kullaniciAdi}
                    onChange={handleFormChange}
                    maxLength={50}
                    minLength={3}
                    pattern="[a-zA-Z0-9_]+"
                    placeholder="Kullanıcı Adı"
                    required
                  />
                  <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    Sadece harf, rakam ve alt çizgi (3-50 karakter)
                  </small>
                </label>
                <label className="form-field">
                  <span>Şifre *</span>
                  <input
                    type="password"
                    name="sifre"
                    value={formState.sifre}
                    onChange={handleFormChange}
                    minLength={6}
                    maxLength={100}
                    placeholder="En az 6 karakter"
                    required
                  />
                  <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    {formState.sifre.length} / 100 karakter - Küçük harf, büyük harf ve rakam içermeli
                  </small>
                </label>
                <label className="form-field">
                  <span>Rol *</span>
                  <select
                    name="rol"
                    value={formState.rol}
                    onChange={handleFormChange}
                    required
                  >
                    {roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                {formState.rol === 'bayi' && (
                  <label className="form-field full-width">
                    <span>Bayi İsmi *</span>
                    <input
                      type="text"
                      name="bayiAdi"
                      value={formState.bayiAdi}
                      onChange={handleFormChange}
                      placeholder="Bayinin tam adı"
                      maxLength={200}
                      required
                    />
                  </label>
                )}
                {(formState.rol === 'tech-person' || formState.rol === 'tech-service') && (
                  <div className="form-field full-width">
                    <span>Teknik Servis *</span>
                    {formState.rol === 'tech-service' && (
                      <p
                        style={{
                          fontSize: '13px',
                          color: 'var(--text-gray)',
                          margin: '0 0 10px 0',
                          lineHeight: 1.5,
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid var(--border-color)',
                          background: 'rgba(0, 0, 0, 0.02)',
                        }}
                      >
                        Her teknik serviste yalnızca <strong>bir</strong> panel (Teknik Servis) hesabı
                        tanımlanabilir. Yeni atama için önce mevcut panel kullanıcısını silin veya rolünü
                        değiştirin; atama başarılı olduğunda kayıt &quot;Teknik Servis Yönetimi&quot;
                        ekranındaki yetkili listede de görünür.
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        {selectedTechService ? (
                          <div className="selected-tech-service">
                            <div>
                              <span className="tech-service-selection-title">
                                Seçili: {selectedTechServiceName}
                              </span>
                              <div className="tech-service-selection-subtitle">
                                {selectedTechService?.kod != null
                                  ? `Kod: ${selectedTechService.kod} · `
                                  : ''}
                                {formatTechServiceLocation(selectedTechService)}
                              </div>
                            </div>
                            <div className="selected-tech-service-actions">
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() => {
                                  const techId =
                                    selectedTechService.id || selectedTechService._id;
                                  if (techId) {
                                    navigate(`/tech-services/${techId}/edit`);
                                  }
                                }}
                              >
                                Teknik servisi düzenle
                              </button>
                              <button
                                type="button"
                                className="secondary-btn"
                                onClick={clearTechServiceSelection}
                              >
                                <i className="fas fa-trash"></i>
                                Temizle
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ 
                            padding: '12px 16px', 
                            border: '1px solid var(--border-color)', 
                            borderRadius: '8px',
                            background: 'var(--bg-white)',
                            fontSize: '14px',
                            color: 'var(--text-light)'
                          }}>
                            Teknik servis seçilmedi
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="secondary-btn"
                        onClick={openTechServiceModal}
                      >
                        <i className="fas fa-search"></i>
                        Teknik Servis Seç
                      </button>
                    </div>
                    <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      {formState.rol === 'tech-person' ? 'Teknisyen' : 'Teknik Servis'} rolü için gereklidir
                    </small>
                  </div>
                )}
                {formState.rol === 'tech-person' && (
                  <label className="form-field full-width">
                    <span>Sipariş Oluşturma Yetkisi</span>
                    <select
                      name="orderCreationPermission"
                      value={formState.orderCreationPermission}
                      onChange={handleFormChange}
                    >
                      {ORDER_CREATION_PERMISSION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="form-field checkbox-field">
                  <input
                    type="checkbox"
                    name="aktif"
                    checked={formState.aktif}
                    onChange={handleFormChange}
                  />
                  <span>Aktif Kullanıcı</span>
                </label>
                <label className="form-field checkbox-field">
                  <input
                    type="checkbox"
                    name="isTest"
                    checked={formState.isTest}
                    onChange={handleFormChange}
                  />
                  <span>Test kullanıcısı</span>
                </label>
              </div>

              {formError && <div className="error-state small">{safeText(formError)}</div>}

              <div className="modal-footer">
                <button type="button" className="secondary-btn" onClick={closeForm}>
                  İptal
                </button>
                <button type="submit" className="primary-btn" disabled={formSubmitting || !isFormValid}>
                  {formSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Teknik Servis Seçim Modal */}
      {isTechServiceModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeTechServiceModal}>
          <div
            className="modal quick-modal modal-tech-service-pick"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '640px', width: '100%' }}
          >
            <div className="modal-header">
              <h2>Teknik Servis Seç</h2>
              <button type="button" className="close-btn" onClick={closeTechServiceModal} aria-label="Kapat">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body modal-body--ts-picker">
              <div className="tech-service-selection tech-service-selection--picker">
                <div className="tech-service-selection-input">
                  <div className="search-box">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      placeholder="Örn: Ankara servis (min 3 karakter, boşluk kullanılabilir)..."
                      value={techServiceSearchInput}
                      onChange={(e) => {
                        setTechServiceSearchInput(
                          sanitizeSearchInput(e.target.value, { trimEdges: false })
                        );
                        if (e.target.value.trim().length >= 3) {
                          setTechServicePickerOpen(true);
                        }
                      }}
                      maxLength={100}
                      autoComplete="off"
                    />
                    {techServiceSearchInput && (
                      <button
                        type="button"
                        className="search-clear-btn"
                        onClick={() => setTechServiceSearchInput('')}
                        aria-label="Aramayı temizle"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
                {techServiceSearchInput.trim().length >= 3 && techServicePickerOpen && (
                  <div className="tech-service-selection-dropdown" role="listbox" aria-label="Teknik servis arama sonuçları">
                    <div className="tech-service-selection-list" role="list">
                      {techServiceSearchDebouncing || techServiceSearchLoading ? (
                        <div className="tech-service-selection-empty tech-service-selection-empty--loading">
                          <span className="ts-picker-spinner" aria-hidden="true" />
                          <span>Sonuçlar getiriliyor…</span>
                        </div>
                      ) : techServiceSearchResults.length === 0 ? (
                        <div className="tech-service-selection-empty tech-service-selection-empty--hint">
                          <i className="fas fa-folder-open"></i>
                          <div className="ts-picker-empty-text">
                            <span className="ts-picker-empty-title">Sonuç yok</span>
                            <span className="ts-picker-empty-sub">Farklı bir arama terimi deneyin.</span>
                          </div>
                        </div>
                      ) : (
                        techServiceSearchResults.map((techService) => {
                          const serviceId = techService.id || techService._id;
                          const selectedId =
                            selectedTechServiceForModal &&
                            (selectedTechServiceForModal.id || selectedTechServiceForModal._id);
                          const isSelected = selectedId && String(selectedId) === String(serviceId);
                          const displayName = techService.ad || 'Bilinmeyen Teknik Servis';
                          const loc = formatTechServiceLocation(techService);
                          return (
                            <button
                              type="button"
                              key={serviceId}
                              className={`tech-service-selection-item ts-picker-row ${isSelected ? 'active' : ''}`}
                              onClick={() => handleTechServiceRowPick(techService)}
                            >
                              <div className="ts-picker-row-body">
                                <div className="ts-picker-row-top">
                                  <span className="ts-picker-name">{displayName}</span>
                                  {techService.kod != null && (
                                    <span className="ts-picker-badge">Kod {techService.kod}</span>
                                  )}
                                </div>
                                {loc ? (
                                  <div className="ts-picker-loc">
                                    <i className="fas fa-map-marker-alt" aria-hidden="true" />
                                    <span>{loc}</span>
                                  </div>
                                ) : null}
                              </div>
                              <div className="ts-picker-check is-on" aria-hidden="true">
                                <i className="fas fa-check" />
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer" style={{ marginTop: '16px' }}>
                <button type="button" className="secondary-btn" onClick={closeTechServiceModal}>
                  İptal
                </button>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={handleTechServiceConfirm}
                  disabled={!selectedTechServiceForModal}
                >
                  <i className="fas fa-check"></i>
                  Seç
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UserManagementPage;


